import { createContext, ReactNode, useContext, useEffect, useReducer, useState } from 'react';
import { useQuery } from 'react-query';
import * as Formatters from '@common/utils/format';
import { DriveTabs } from '@common/constants/logbook';
import { AdditionalFilters } from '@common/interfaces/LogbookRepository';
import { useGlobalContext, useLogbookContext } from '@context';
import { useRepository } from '../repository.context';

type Filter = {
  title: string;
  options: { key: string; value: string }[];
  selected?: string;
};

type FiltersState = {
  filters: Filter[];
};

export enum FilterActionTypes {
  CHANGE_FILTER = 'change-filter',
  CLEAR_ALL = 'clear-all',
  CLEAR_ONE = 'clear-one',
  INITIAL = 'initial-filters',
}

type FilterAction =
  | { type: FilterActionTypes.CHANGE_FILTER; payload: { title: string; option: string } }
  | { type: FilterActionTypes.CLEAR_ALL }
  | { type: FilterActionTypes.CLEAR_ONE; payload: string }
  | { type: FilterActionTypes.INITIAL; payload: Filter[] };

const initialFilterState: FiltersState = {
  filters: [],
};

function tableReducer(state: FiltersState, action: FilterAction): FiltersState {
  switch (action.type) {
    case FilterActionTypes.INITIAL: {
      return { filters: action.payload };
    }
    case FilterActionTypes.CLEAR_ALL: {
      return {
        ...state,
        filters: state.filters.map((i) => ({
          ...i,
          selected: undefined,
        })),
      };
    }
    case FilterActionTypes.CLEAR_ONE: {
      return {
        ...state,
        filters: state.filters.map((i) => {
          if (i.title === action.payload) {
            return {
              ...i,
              selected: undefined,
            };
          }
          return i;
        }),
      };
    }
    case FilterActionTypes.CHANGE_FILTER:
      return {
        ...state,
        filters: state.filters.map((i) => {
          if (i.title === action.payload.title) {
            return {
              ...i,
              selected: action.payload.option,
            };
          }
          return i;
        }),
      };
    default:
      return initialFilterState;
  }
}

const initialState = {
  filtersState: initialFilterState,
  isFilterLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  filterDispatch: (_: FilterAction) => {},
  selectedFilters: [] as { title: string; selected: string }[],
  selectFiltersForApi: () => {},
};

const LogbookFilterContext = createContext(initialState);

function LogbookFilterProvider({ children }: { children: ReactNode }) {
  const [filtersState, filterDispatch] = useReducer(tableReducer, initialFilterState);
  const [selectedFilters, setSelectedFilters] = useState<{ title: string; selected: string }[]>([]);

  const { logbookRepository } = useRepository();
  const { selectedTab, datePeriod, tableDispatch } = useLogbookContext();
  const { facility } = useGlobalContext();

  const selectFiltersForApi = () => {
    const filters = filtersState.filters
      .filter((i) => i.selected)
      .map((i) => ({
        title: i.title,
        selected: i.options.find((j) => j.value === i.selected)!.key,
      }));

    const res: AdditionalFilters = {};

    filters.forEach((i) => {
      res[i.title] = i.selected;
    });

    return res;
  };

  const { data, isLoading } = useQuery(
    [
      'filters',
      selectedTab,
      Formatters.dateFormatForAPI(datePeriod.last),
      Formatters.dateFormatForAPI(datePeriod.first),
    ],
    () =>
      logbookRepository.getFilters(
        facility.facilityId,
        selectedTab === DriveTabs.toDo,
        Formatters.dateFormatForAPI(datePeriod.last),
        Formatters.dateFormatForAPI(datePeriod.first),
      ),
    {
      onError: () => {
        filterDispatch({
          type: FilterActionTypes.INITIAL,
          payload: filtersState.filters.map((i) => ({
            ...i,
            selected: undefined,
            options: [],
          })),
        });
      },
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (data) {
      filterDispatch({
        type: FilterActionTypes.INITIAL,
        payload: Object.keys(data).map((i) => ({
          title: i,
          options: data[i],
        })),
      });
    }
  }, [data, isLoading]);

  useEffect(() => {
    setSelectedFilters(
      filtersState.filters
        .filter((i) => i.selected)
        .map((i) => ({ title: i.title, selected: i.selected || '' })),
    );
    selectFiltersForApi();
    tableDispatch({ type: 'initial', payload: selectedTab });
  }, [filtersState]);

  return (
    <LogbookFilterContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        filtersState,
        filterDispatch,
        selectedFilters,
        selectFiltersForApi,
        isFilterLoading: isLoading,
      }}
    >
      {children}
    </LogbookFilterContext.Provider>
  );
}

const useLogbookFilterContext = () => useContext(LogbookFilterContext);

export { LogbookFilterProvider, useLogbookFilterContext as useFilterContext };
